import { useEffect } from "react";

// Favicon provided via authoritative embed_code SEO data.
export const FAVICON_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg";

interface FaqItem {
  q: string;
  a: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface ArticleArgs {
  headline: string;
  description: string;
  author: string;
  dateModified: string;
  url: string;
  image?: string;
}

export function buildOrganizationSchema(
  name: string,
  url: string,
  logo: string,
): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url,
    logo,
  };
}

export function buildWebsiteSchema(
  name: string,
  url: string,
  description: string,
): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url,
    description,
  };
}

export function buildFaqSchema(items: FaqItem[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function buildArticleSchema(args: ArticleArgs): object {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.headline,
    description: args.description,
    author: { "@type": "Organization", name: args.author },
    dateModified: args.dateModified,
    mainEntityOfPage: args.url,
    image: args.image,
  };
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.path,
    })),
  };
}

export function useJsonLd(id: string, data: object | null): void {
  const serialized = data ? JSON.stringify(data) : "";

  useEffect(() => {
    if (!data) {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      return;
    }

    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = serialized;

    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, serialized]);
}